
import React from "react";
import { Navigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { toast } from "sonner";
import { Sun, AlertCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

const AdminLogin = () => {
  const { user, isAdmin, loading, authChangeProcessed, login } = useAuth();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // Show loading while auth is being processed
  if (loading || !authChangeProcessed) {
    return (
      <div className="min-h-screen bg-warm-cream flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-b-transparent border-peach-orange rounded-full"></div>
      </div>
    );
  }

  // Redirect if already authenticated and is admin
  if (user && isAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Redirect to main site if authenticated but not admin
  if (user && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    const success = await login(values.email, values.password);
    
    if (success) {
      // The auth guard will handle the redirect based on user role
      toast.success("Login successful!");
    } else {
      toast.error("Invalid credentials or insufficient permissions");
    }
  };

  return (
    <div className="min-h-screen bg-warm-cream flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg border-peach-orange/20">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-2">
            <Sun className="h-8 w-8 text-peach-orange animate-sunshine" />
          </div>
          <CardTitle className="text-2xl text-sunset-dark">Admin Login</CardTitle>
          <CardDescription>
            Enter your admin credentials to access the dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 p-3 bg-peach-orange/10 border border-peach-orange/20 rounded-md">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-peach-orange mt-0.5 flex-shrink-0" />
              <div className="text-sm text-sunset-medium">
                <p className="font-medium">Admin Access Required</p>
                <p>Only users with admin role can access the admin dashboard.</p>
              </div>
            </div>
          </div>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="admin@example.com" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="••••••••" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button 
                type="submit" 
                className="w-full bg-peach-orange hover:bg-coral-pink"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? "Signing in..." : "Sign In"}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="flex justify-center">
          <Button variant="link" onClick={() => window.location.href = "/"}>
            Return to main site
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default AdminLogin;
